import { useState, useMemo, useRef } from "react";
import { Search, X, Check, Package, Circle, Copy, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";
import { toast } from "sonner";
import GarmentFabricSection, { visibleGarmentsFor } from "@/components/GarmentFabricSection";

// Lining types
const LINING_TYPES = ["Full", "Half", "Quarter", "Unlined"];

const MTMFabricStep = ({
  fabricSelection,
  setFabricSelection,
  liningSelection,
  setLiningSelection,
  liningType,
  setLiningType,
  // Piping
  pipingSelection,
  setPipingSelection,
  // Buttons
  jacketButtonSelection,
  setJacketButtonSelection,
  vestButtonSelection,
  setVestButtonSelection,
  vestMatchesJacket,
  setVestMatchesJacket,
  // Garment flags
  includeJacket,
  includeTrouser,
  includeVest,
  // Per-garment selections (Quick-Order-style)
  garmentType,
  fabricByGarment = {},
  setFabricByGarment,
  liningByGarment = {},
  setLiningByGarment,
  buttonByGarment = {},
  setButtonByGarment,
}) => {
  const { FABRIC_DATA } = useFabricCatalog();
  const [showPipingDialog, setShowPipingDialog] = useState(false);
  const [pipingSearch, setPipingSearch] = useState("");
  const [pipingBookFilter, setPipingBookFilter] = useState("");
  // Copy to clipboard state — used by the Selection Summary chips
  const [copiedCode, setCopiedCode] = useState(null);

  // Tracks the last button code pushed by Match Buttons. Future Match clicks
  // skip garments whose current value differs from this last-cascaded code
  // (= a manual override). null on first Match → always overwrites.
  const buttonMatchSourceRef = useRef(null);

  // Copy RC code to clipboard
  const copyToClipboard = async (code, type) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success(`${type} code copied: ${code}`);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  // Filtered piping list (sourced from linings catalog) — no slice cap; honours Book filter.
  const filteredPipings = useMemo(() => {
    let linings = FABRIC_DATA.linings || [];
    if (pipingBookFilter) {
      linings = linings.filter((l) => (l.book || "") === pipingBookFilter);
    }
    if (!pipingSearch) return linings;
    const search = pipingSearch.toLowerCase();
    return linings.filter((l) => {
      const searchFields = [l.rc_code, l.book, l.category, l.color, l.pattern]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchFields.includes(search);
    });
  }, [pipingSearch, pipingBookFilter, FABRIC_DATA]);

  // Piping book filter options
  const pipingBookOptions = useMemo(() => {
    const books = new Set();
    for (const l of FABRIC_DATA.linings || []) {
      if (l.book) books.add(l.book);
    }
    return Array.from(books).sort();
  }, [FABRIC_DATA]);


  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">Fabric & Lining</h2>
        <p className="text-slate-400">Select fabric, lining, piping, and buttons</p>
      </div>

      {/* Per-garment Fabric / Lining / Buttons — same UX as Quick Order.
          Drives the per-garment maps; a sync layer in AdminPOSPage derives
          the canonical fabricSelection/liningSelection/jacketButtonSelection
          downstream consumers still depend on. */}
      {setFabricByGarment && (
        <div className="bg-[#111c2e] rounded-xl p-6 border border-white/10" data-testid="pos-garment-fabric-section-wrapper">
          <GarmentFabricSection
            garmentType={garmentType}
            fabricByGarment={fabricByGarment}
            setFabricByGarment={setFabricByGarment}
            liningByGarment={liningByGarment}
            setLiningByGarment={setLiningByGarment}
            buttonByGarment={buttonByGarment}
            onButtonChange={(g, v) => {
              if (typeof setButtonByGarment === "function") {
                setButtonByGarment((prev) => ({ ...(prev || {}), [g]: v }));
              }
            }}
            buttonSourceLabel="Jacket button"
            buttonSourceValue={buttonByGarment?.jacket || ""}
            onMatchAllButtons={() => {
              // Source = the Jacket button code the user has already picked
              // in the Jacket sub-block. Pant-only or Shirt-only orders use
              // the pant/shirt button as the source instead.
              const visible = visibleGarmentsFor(garmentType);
              const src =
                buttonByGarment?.jacket ||
                buttonByGarment?.overcoat ||
                buttonByGarment?.pant ||
                buttonByGarment?.vest ||
                buttonByGarment?.shirt;
              if (!src || typeof setButtonByGarment !== "function") return;
              setButtonByGarment((prev) => {
                const cur = prev || {};
                const lastSrc = buttonMatchSourceRef.current;
                const next = { ...cur };
                // Cascade ONLY into garments visible for the active garment
                // type. Empty slots always fill. Slots that match the last
                // cascade source also fill. Anything else = manual override
                // and is preserved. First Match (lastSrc=null) overwrites
                // every visible garment.
                for (const g of visible) {
                  const c = cur[g];
                  if (!c) { next[g] = src; continue; }
                  if (lastSrc === null) { next[g] = src; continue; }
                  if (c === lastSrc)   { next[g] = src; continue; }
                  // c is a manual override → skip
                }
                buttonMatchSourceRef.current = src;
                return next;
              });
            }}
            testIdPrefix="pos-garment-fabric"
          />

          {/* ORDER OPTIONS — Lining Type + Piping (Quick Order doesn't have
              these, but POS needs them. Compact 2-col row below the
              per-garment grid). */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-5 bg-blue-500 rounded-full" />
              <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300">Order Options</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Lining Type</label>
                <Select value={liningType} onValueChange={setLiningType}>
                  <SelectTrigger className="bg-[#0a1628] border-white/10 h-10" data-testid="pos-lining-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINING_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Piping</label>
                <Button
                  variant="outline"
                  className="w-full justify-between h-10 bg-[#0a1628] border-white/10"
                  onClick={() => setShowPipingDialog(true)}
                  data-testid="pos-piping-trigger"
                >
                  {pipingSelection ? (
                    <span className="font-mono text-purple-400 text-sm">{pipingSelection.rc_code}</span>
                  ) : (
                    <span className="text-slate-500 text-sm">Select from RCMTM Catalog...</span>
                  )}
                  <Search size={14} className="text-slate-400" />
                </Button>
                {pipingSelection && (
                  <button
                    onClick={() => setPipingSelection(null)}
                    className="mt-1 text-[10px] text-slate-500 hover:text-red-400 flex items-center gap-1"
                  >
                    <X size={10} /> Clear piping
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Summary */}
      {(fabricSelection || liningSelection || pipingSelection || jacketButtonSelection) && (
        <div className="bg-[#111c2e] rounded-xl p-4 border border-white/10">
          <h4 className="text-sm font-medium text-slate-400 mb-2">SELECTION SUMMARY</h4>
          <div className="flex flex-wrap gap-3">
            {fabricSelection && (
              <div className="flex items-center gap-2 px-2 py-1 bg-amber-500/10 rounded">
                <Check size={12} className="text-green-400" />
                <span className="text-xs">Fabric: {fabricSelection.rc_code}</span>
              </div>
            )}
            {liningSelection && (
              <div className="flex items-center gap-2 px-2 py-1 bg-blue-500/10 rounded">
                <Check size={12} className="text-green-400" />
                <span className="text-xs">Lining: {liningSelection.rc_code} ({liningType})</span>
              </div>
            )}
            {pipingSelection && (
              <div className="flex items-center gap-2 px-2 py-1 bg-purple-500/10 rounded">
                <Check size={12} className="text-green-400" />
                <span className="text-xs">Piping: {pipingSelection.rc_code}</span>
              </div>
            )}
            {jacketButtonSelection && (
              <div className="flex items-center gap-2 px-2 py-1 bg-amber-500/10 rounded">
                <Check size={12} className="text-green-400" />
                <span className="text-xs">Jacket Buttons: {jacketButtonSelection.code}</span>
              </div>
            )}
          </div>
        </div>
      )}



      {/* Piping Selection Dialog */}
      <Dialog open={showPipingDialog} onOpenChange={setShowPipingDialog}>
        <DialogContent className="bg-[#111c2e] border-white/10 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select Piping from RCMTM Catalog</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={pipingSearch}
                  onChange={(e) => setPipingSearch(e.target.value)}
                  placeholder="Search by RC code, book or color..."
                  className="pl-10 bg-[#0a1628] border-white/10"
                  data-testid="pos-piping-search"
                />
              </div>
              <Select
                value={pipingBookFilter || "__all__"}
                onValueChange={(v) => setPipingBookFilter(v === "__all__" ? "" : v)}
              >
                <SelectTrigger
                  className="w-full md:w-56 bg-[#0a1628] border-white/10"
                  data-testid="pos-piping-book-filter"
                >
                  <SelectValue placeholder="All books" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All books ({pipingBookOptions.length})</SelectItem>
                  {pipingBookOptions.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {filteredPipings.map((piping, idx) => (
                  <button
                    key={`${piping.rc_code}-${idx}`}
                    onClick={() => {
                      setPipingSelection(piping);
                      setShowPipingDialog(false);
                    }}
                    className={`p-3 rounded-lg border text-left transition-all hover:border-purple-500/50 ${
                      pipingSelection?.rc_code === piping.rc_code 
                        ? "bg-purple-500/20 border-purple-500" 
                        : "bg-[#0a1628] border-white/10"
                    }`}
                  >
                    <p className="font-mono font-bold text-purple-400 text-sm">{piping.rc_code}</p>
                    <p className="text-xs text-slate-400 truncate">{piping.category || piping.book}</p>
                    {piping.is_new && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-1 rounded">NEW</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center">
              Showing {filteredPipings.length} linings for piping
            </p>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default MTMFabricStep;
